/* ==========================================================================
   JUVERTO CART BEHAVIOUR
   Owns the live gift-note card, the collapsed row that offers it, the $0.00
   gift note LINE ITEM behind it, the leave animation on a removed row, and
   the reduced-motion override for the drawer itself.

   IT IS NOT ONLY THE CART. The same gift note snippet renders in the product
   page buy box, under Add to Cart. Everything below is written to work from
   any number of instances on a page: every listener is delegated from
   document, mirror() keeps every [data-juv-note-input] carrying one value,
   and syncLine() is the single writer. There is no product-page variant of
   any of this, and there must not be one.

   WHY A LINE ITEM AND NOT A CART ATTRIBUTE. This used to write
   attributes["Gift note"]. That reached the order, but a cart attribute is
   not a line item and Shopify's checkout will not draw it, so the customer
   reached the payment step with no evidence the message existed.

   THE LINE IS A SINGLETON. syncLine() looks the line up by handle first and
   only ever calls cart/add.js when there is none; every later edit is a
   cart/change.js on the key it found. Two Gift Note lines cannot be produced
   by any sequence of edits from any surface.

   THE LINE IS NOT DRAWN AS A CART ROW. It exists in the cart and shows at
   checkout, but the message already appears in the gift note section, so a
   second "Gift Note / Free / <message>" row between the products and the
   totals was a duplicate. There used to be an upsertNoteRow() here that built
   exactly that row; it is gone, and the two cart sections skip the line when
   they render .juv-cart__items. Do not add it back.

   WHERE THE SECTION SITS IS NOT DECIDED HERE. The gift note moved below the
   product lines on 2026-08-25; that order is set in Liquid, in
   sections/cart-drawer.liquid and sections/main-cart.liquid. This file never
   inserts, moves or reorders the section, and an earlier request to move it
   was missed because it was looked for in here.

   ==========================================================================
   form.submit() DOES NOT INCLUDE THE SUBMIT BUTTON. THIS BLOCKED ORDERS.
   ==========================================================================

   REPORTED: on mobile, tapping Checkout reloaded the cart instead of going to
   checkout. The markup was never the problem -- it is
   <button type="submit" name="checkout"> inside <form action="/cart"
   method="POST">, and with no note pending the submit event carried
   submitter = "checkout" and worked.

   The interception below is what broke it. Measured, with a note pending:

     click defaultPrevented   true
     form.submit() called     1
     submit EVENT fired       0        <- form.submit() bypasses it entirely
     payload keys             ["updates[]"]
     payload has "checkout"   FALSE

   A submit button's name/value is contributed by the ACTIVATION, not by the
   form's contents. form.submit() is not an activation, so `checkout` was
   never posted -- and a POST to /cart carrying only updates[] is a cart
   update, which Shopify answers with a redirect back to /cart. Exactly the
   reported symptom, and only when a note was pending, which is exactly when
   the customer has just typed one.

   requestSubmit(button) IS the activation: it fires the submit event and
   contributes the submitter. The hidden-input fallback covers Safari below
   16, which has no requestSubmit.

   ==========================================================================
   TWO LIMITS, WHICHEVER COMES FIRST, BOTH READ OFF THE MARKUP (2026-09-17)
   ==========================================================================

   300 characters, and 8 rendered lines on the card. Both numbers are
   written ONCE, in snippets/juverto-gift-note.liquid, and arrive here as
   data-juv-max-chars / data-juv-max-rows on the note root (maxlength on
   the textarea is the no-JS backstop). This file carries no number of its
   own beyond the fallbacks in capFor() and rowsFor().

   THE ROW CAP IS MEASURED, NOT COUNTED. rowsOf() reads the rendered card
   paragraph's height against its line-height, so it sees real wrapped
   lines as laid out PLUS every typed line break and empty line. The
   2026-09-15 build had dropped the cap and let long or break-heavy
   messages clip; on the new card the region ends at the JUVERTO wordmark,
   so a ninth line would print over the brand.

   NOTHING IS TRIMMED SILENTLY:
     - a keystroke or paste that would pass 300 characters is refused in
       beforeinput, before the browser inserts (or truncates) anything;
     - an edit that begins a ninth rendered line is put back to the last
       accepted value in render(), caret restored;
     - either way .juv-note__limit under the counter says which cap it was,
       and it stays while the message sits exactly at a cap.
   Mirrored repaints (isProgrammatic) skip the guard: they carry a value
   another instance already accepted, and a closed drawer has no layout
   to measure against anyway.

   A TRAILING LINE BREAK IS A LINE. The card paragraph gets a zero-width
   space after a value ending in "\n" so the empty ninth line the customer
   just opened is laid out and counted; it never reaches the cart, because
   every write trims.

   Every write to the cart slices to the character cap first. Every Liquid
   render of the message truncates to it. There is no hook that lets a
   theme validate a line item property on Shopify's side; see the snippet's
   doc block.

   THE BOX GROWS. autosize() sets the textarea's height to its scrollHeight
   on every paint, so the writing box follows the text instead of scrolling
   inside a fixed frame.

   THERE IS NO AUTO-SHRINK on the card. An earlier version scaled the type
   down to make long messages fit, which made the preview lie about the
   print. The row cap gives way instead.

   NO requestAnimationFrame ANYWHERE IN HERE. The preview environment this is
   verified in runs with visibilityState "hidden", where rAF never fires.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__juvertoCart) return;
  window.__juvertoCart = true;

  var NOTE_HANDLE = 'juverto-gift-note';
  var LEGACY_ATTR = 'Gift note';

  /* The caps live on the note root (snippets/juverto-gift-note.liquid).
     These are only the fallbacks for markup that predates them. */
  var FALLBACK_CAP = 300;
  var FALLBACK_ROWS = 8;
  var SAVE_DELAY = 700;

  function rootOf(input) {
    return input && input.closest ? input.closest('[data-juv-note]') : null;
  }

  function capFor(input) {
    var r = rootOf(input);
    var n = r && parseInt(r.getAttribute('data-juv-max-chars'), 10);
    if (!(n > 0)) n = input && parseInt(input.getAttribute('maxlength'), 10);
    return n > 0 ? n : FALLBACK_CAP;
  }

  function rowsFor(input) {
    var r = rootOf(input);
    var n = r && parseInt(r.getAttribute('data-juv-max-rows'), 10);
    return n > 0 ? n : FALLBACK_ROWS;
  }

  /* Any input on the page will do for the cap when there is no specific one
     to hand: they all render the same maxlength. */
  function cap() {
    return capFor(document.querySelector('[data-juv-note-input]'));
  }

  var saveTimer = null;
  var writing = false;
  var syncing = false;
  var reapplying = false;

  function reduced() {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  function allInputs() {
    return document.querySelectorAll('[data-juv-note-input]');
  }

  function root() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function postJSON(path, body) {
    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  /* ---------------------------------------------------------------------
     THE PREVIEW AND THE COLLAPSED ROW
     --------------------------------------------------------------------- */

  function fieldsFor(input) {
    var rootEl = input.closest('[data-juv-note]');
    if (!rootEl) return null;
    return {
      root: rootEl,
      text: rootEl.querySelector('[data-juv-note-text]'),
      ghost: rootEl.querySelector('[data-juv-note-ghost]'),
      counter: rootEl.querySelector('[data-juv-note-counter]'),
      message: rootEl.querySelector('[data-juv-note-message]'),
      action: rootEl.querySelector('[data-juv-note-action]'),
      limit: rootEl.querySelector('[data-juv-note-limit]')
    };
  }

  /* The writing box follows the text. Height is reset first so it can also
     shrink when lines are deleted; scrollHeight then measures the content. */
  function autosize(input) {
    if (!input || input.tagName !== 'TEXTAREA') return;
    input.style.height = 'auto';
    input.style.height = input.scrollHeight + 'px';
  }

  function paint(input) {
    var parts = fieldsFor(input);
    if (!parts) return null;

    var max = capFor(input);
    var value = String(input.value || '');
    if (value.length > max) {
      value = value.slice(0, max);
      input.value = value;
    }
    autosize(input);

    var empty = value.trim() === '';

    /* EVERY WRITE BELOW IS GUARDED: nothing is written unless it changes.

       This is not tidiness. observe() watches the whole <body> for mutations
       and repaints on each batch, and a repaint that rewrites the same text
       and the same attributes IS a batch of mutations -- the observer fired
       again 40ms later, forever. Measured 2026-09-13: 340 DOM mutations in
       three idle seconds on every product page, a style recalc each time,
       and Lighthouse put it down as seconds of Style & Layout. The `writing`
       flag never caught it because mutation records are delivered after
       paint() has already returned and cleared it. */
    writing = true;
    if (parts.text) {
      /* The zero-width space makes a trailing line break lay out as an
         (empty) line so rowsOf() counts it. See the doc block. */
      setText(parts.text, /\n$/.test(value) ? value + '​' : value);
      setAttr(parts.text, 'data-juv-placeholder', empty ? 'true' : 'false');
    }
    if (parts.ghost && parts.ghost.hidden !== !empty) parts.ghost.hidden = !empty;

    /* THE COLLAPSED ROW. The thumbnail never changes -- it is the invitation
       and it is present in every state. What swaps is the label ("Add a gift
       note" / "Your gift note", both in the markup, chosen by this attribute
       in CSS) and Free / Edit. The row NEVER shows the words themselves
       (2026-09-17): the card is the only preview. */
    setAttr(parts.root, 'data-juv-has-note', empty ? 'false' : 'true');
    /* THE TWO LABELS ARE NOT HARDCODED ANY MORE. They are theme settings
       (Theme settings > Gift note), written onto the root by
       snippets/juverto-gift-note.liquid as data attributes, because this file
       is a static asset and cannot read Liquid. The defaults below only apply
       if the markup predates that change. */
    if (parts.action) {
      var freeLabel = parts.root.getAttribute('data-juv-free-label') || 'Free';
      var editLabel = parts.root.getAttribute('data-juv-edit-label') || 'Edit';
      setText(parts.action, empty ? freeLabel : editLabel);
    }

    if (parts.counter) {
      setText(parts.counter, value.length + ' / ' + max);
      /* The "nearly full" warning scales with the cap rather than sitting at a
         fixed 20 characters. */
      setAttr(parts.counter, 'data-juv-near', value.length >= max - 40 ? 'true' : 'false');
      setAttr(parts.counter, 'data-juv-full', value.length >= max ? 'true' : 'false');
    }
    writing = false;

    return parts;
  }

  function setText(el, value) {
    if (el.textContent !== value) el.textContent = value;
  }

  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  /* ---------------------------------------------------------------------
     THE ROW CAP
     --------------------------------------------------------------------- */

  /* Rendered lines on the card: the paragraph's laid-out height over its
     line-height. 0 when there is no layout to measure (a closed drawer, a
     collapsed panel) -- callers treat 0 as "cannot tell", never as "fits". */
  function rowsOf(parts) {
    var el = parts && parts.text;
    if (!el || !el.clientWidth) return 0;
    var cs = getComputedStyle(el);
    var lh = parseFloat(cs.lineHeight);
    if (!(lh > 0)) return 0;
    var h = el.getBoundingClientRect().height;
    if (!(h > 0)) return 0;
    return Math.round(h / lh);
  }

  /* The one quiet line under the counter. `kind` is 'chars' or 'rows';
     null hides it. The sentences are theme settings, written onto the root
     by the snippet. */
  function showLimit(parts, kind) {
    if (!parts || !parts.limit) return;
    if (!kind) {
      if (!parts.limit.hidden) parts.limit.hidden = true;
      return;
    }
    var text = parts.root.getAttribute(kind === 'rows' ? 'data-juv-limit-rows' : 'data-juv-limit-chars') || '';
    setText(parts.limit, text);
    if (parts.limit.hidden) parts.limit.hidden = false;
  }

  /* Which cap, if any, the value is sitting on right now. Chars first: it
     is the one a customer can see counting up. */
  function limitState(input, parts) {
    var value = String(input.value || '');
    if (value.length >= capFor(input)) return 'chars';
    var rows = rowsOf(parts);
    if (rows && rows >= rowsFor(input)) return 'rows';
    return null;
  }

  function remember(input) {
    input.__juvLastGood = {
      value: String(input.value || ''),
      start: input.selectionStart,
      end: input.selectionEnd
    };
  }

  /* Paint, then -- for an edit the customer made -- check the card. An edit
     that has begun a line past the cap is put back to the last accepted
     value with the caret where it was, and the limit line says why.
     Programmatic repaints (mirror, syncAll) carry an already-accepted value
     and skip the guard. */
  function render(input, isProgrammatic) {
    var parts = paint(input);
    if (!parts) return;

    if (isProgrammatic) {
      remember(input);
      showLimit(parts, limitState(input, parts));
      return;
    }

    var rows = rowsOf(parts);
    if (rows && rows > rowsFor(input) && input.__juvLastGood) {
      var last = input.__juvLastGood;
      input.value = last.value;
      paint(input);
      try { input.setSelectionRange(last.start, last.end); } catch (e) { /* not focused */ }
      showLimit(parts, 'rows');
      return;
    }

    remember(input);
    showLimit(parts, limitState(input, parts));
  }

  /* ONE VALUE, EVERY SURFACE. This is what makes the product page field and
     the drawer field two views of the same message rather than two fields.
     Both directions are covered because it runs from a delegated listener on
     document: whichever one the customer types into is the source, and every
     other instance is repainted programmatically (isProgrammatic = true, so
     the line guard does not fight a value it did not cause). */
  function mirror(source) {
    var inputs = allInputs();
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i] === source) continue;
      if (inputs[i].value === source.value) continue;
      inputs[i].value = source.value;
      render(inputs[i], true);
    }
  }

  /* ---------------------------------------------------------------------
     THE CART LINE
     --------------------------------------------------------------------- */

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function noteLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].handle === NOTE_HANDLE) return cart.items[i];
    }
    return null;
  }

  function realItemCount(cart) {
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].handle !== NOTE_HANDLE) n++;
    }
    return n;
  }

  /* THE HEADER DOT FOLLOWS THE REAL COUNT (2026-09-16). The theme's
     <cart-dot> lights on cart.item_count > 0, which counts the $0 note line,
     so a bag holding only the note -- the state observe() below deletes on
     load -- kept the dot lit after the last product was removed. This runs
     after the vendor's own cart:change listener (this file loads after
     theme.js) and after every write this file makes, and it counts products
     only. sections/header.liquid renders the initial state the same way. */
  function syncDot(cart) {
    if (!cart || !cart.items) return;
    var on = realItemCount(cart) > 0;
    var dots = document.querySelectorAll('cart-dot');
    for (var i = 0; i < dots.length; i++) dots[i].classList.toggle('is-visible', on);
  }

  document.addEventListener('cart:change', function (event) {
    syncDot(event && event.detail && event.detail.cart);
  });

  function variantId() {
    var el = document.querySelector('[data-juv-note-variant]');
    if (!el) return null;
    var v = parseInt(el.getAttribute('data-juv-note-variant'), 10);
    return isNaN(v) ? null : v;
  }

  /* THE ONLY WRITER. Every surface funnels through here.

     The lookup-then-branch is what enforces the singleton: cart/add.js is
     reached only when noteLine() found nothing, and every other path is a
     cart/change.js against the key it already has. */
  function syncLine(text) {
    if (syncing) return Promise.resolve();
    syncing = true;

    /* Last line of defence before anything is written: never more than the
       cap reaches the line item property, whatever the caller passed. */
    text = String(text || '').slice(0, cap()).trim();

    return getCart().then(function (cart) {
      var line = noteLine(cart);
      var others = realItemCount(cart);
      var vid = variantId();

      var cleanup = cart.attributes && cart.attributes[LEGACY_ATTR]
        ? postJSON('cart/update.js', { attributes: (function () { var a = {}; a[LEGACY_ATTR] = ''; return a; })() })
        : Promise.resolve();

      return cleanup.then(function () {
        if (others === 0) {
          if (line) return postJSON('cart/change.js', { id: line.key, quantity: 0 });
          return null;
        }

        if (text) {
          if (!line) {
            if (!vid) return null;
            return postJSON('cart/add.js', {
              items: [{ id: vid, quantity: 1, properties: { Message: text } }]
            });
          }
          if ((line.properties && line.properties.Message) !== text) {
            return postJSON('cart/change.js', {
              id: line.key,
              quantity: 1,
              properties: { Message: text }
            });
          }
          return null;
        }

        if (line) return postJSON('cart/change.js', { id: line.key, quantity: 0 });
        return null;
      });
    }).catch(function () { /* the next edit retries */ })
      .then(function () { syncing = false; });
  }

  function queueSync(input) {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(function () {
      saveTimer = null;
      syncLine(String(input.value || '').slice(0, capFor(input)).trim());
    }, SAVE_DELAY);
  }

  function flushSync(input) {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = null;
    return syncLine(String(input.value || '').slice(0, capFor(input)).trim());
  }

  /* ---------------------------------------------------------------------
     A MESSAGE WRITTEN BEFORE THERE IS ANYTHING TO GIVE

     syncLine() deliberately refuses to create the note line while the cart
     holds no real products, and observe() deletes one it finds: a bag
     containing only a free note is an empty bag, and the cart renders its
     empty state for exactly that case.

     That rule is right in the cart and incomplete on the PRODUCT PAGE, where
     the customer now meets the field BEFORE they have added anything. Left
     alone, a message typed first and added second would be silently dropped.

     So the message simply waits in the textarea -- nothing is queued, stored
     or persisted, the field IS the pending state -- and is re-applied the
     moment an add succeeds. By then others >= 1 and the normal path creates
     the line.

     This wraps fetch rather than listening for a cart event because the add
     is performed by the vendor's own product form component and this theme
     publishes no documented event for it. Two guards matter: `reapplying`
     stops the recursion, since syncLine() itself posts to cart/add.js, and
     the reapply is deferred so any in-flight syncLine() has released
     `syncing` before this one asks for it.
     --------------------------------------------------------------------- */

  function pendingText() {
    var inputs = allInputs();
    for (var i = 0; i < inputs.length; i++) {
      var t = String(inputs[i].value || '').slice(0, capFor(inputs[i])).trim();
      if (t) return t;
    }
    return '';
  }

  (function wrapAddToCart() {
    var native = window.fetch;
    if (typeof native !== 'function') return;

    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { /* opaque request object; treat as not-an-add */ }

      var isAdd = !reapplying && /\/cart\/add(\.js)?(\?|$)/.test(url);
      var result = native.apply(this, arguments);

      if (!isAdd || !result || typeof result.then !== 'function') return result;

      return result.then(function (response) {
        try {
          if (response && response.ok) {
            var text = pendingText();
            if (text) {
              reapplying = true;
              setTimeout(function () {
                syncLine(text).then(function () { reapplying = false; });
              }, 250);
            }
          }
        } catch (e) { reapplying = false; }
        return response;
      });
    };
  })();

  /* The surface the customer last typed into is the source of truth for
     every other instance on the page -- see syncAll(). */
  var lastTyped = null;

  /* THE CHARACTER CAP, BEFORE THE BROWSER TOUCHES THE VALUE. beforeinput
     carries what is about to be inserted (typed text, a line break, a
     paste, a drop); if the selection it replaces plus that text would pass
     the cap, the insert is cancelled whole and the limit line shows. This
     is what stops maxlength from silently trimming a long paste. Deletions
     and unknown insert types (IME composition) are left to the browser. */
  document.addEventListener('beforeinput', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-juv-note-input]')) return;
    if (!input.__juvLastGood) remember(input);

    var type = event.inputType || '';
    if (type.indexOf('insert') !== 0) return;

    var data = null;
    if (type === 'insertLineBreak' || type === 'insertParagraph') {
      data = '\n';
    } else if (typeof event.data === 'string') {
      data = event.data;
    } else if (event.dataTransfer) {
      try { data = event.dataTransfer.getData('text/plain'); } catch (e) { data = null; }
    }
    if (data === null) return;

    var selected = (input.selectionEnd || 0) - (input.selectionStart || 0);
    var next = String(input.value || '').length - selected + data.length;
    if (next <= capFor(input)) return;

    event.preventDefault();
    var parts = fieldsFor(input);
    if (parts) showLimit(parts, 'chars');
  });

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-juv-note-input]')) return;
    lastTyped = input;
    render(input, false);
    mirror(input);
    queueSync(input);
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-juv-note-input]')) return;
    mirror(input);
    flushSync(input);
  });

  document.addEventListener('blur', function (event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-juv-note-input]')) return;
    flushSync(input);
  }, true);

  function inputForSubmit(target) {
    var form = target.closest('form');
    if (form) {
      var inside = form.querySelector('[data-juv-note-input]');
      if (inside) return inside;
      var surface = form.closest('.juv-cart-page, cart-drawer');
      if (surface) {
        var near = surface.querySelector('[data-juv-note-input]');
        if (near) return near;
      }
    }
    return document.querySelector('[data-juv-note-input]');
  }

  /* RESUBMIT AS AN ACTIVATION, NOT AS form.submit().

     form.submit() omits the submit button's name/value, so `checkout` never
     reached /cart and Shopify redirected back to the cart. See the block at
     the top of this file -- this cost real orders. */
  function submitAsCheckout(form, button) {
    if (!form) return;

    if (typeof form.requestSubmit === 'function' && button && button.form === form) {
      try {
        form.requestSubmit(button);
        return;
      } catch (e) { /* fall through */ }
    }

    /* Safari below 16 has no requestSubmit. Name the intent explicitly so the
       POST body still carries it. */
    var hidden = form.querySelector('input[type="hidden"][name="checkout"]');
    if (!hidden) {
      hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'checkout';
      hidden.value = '';
      form.appendChild(hidden);
    }
    form.submit();
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var trigger = t.closest('[name="checkout"], .juv-cart__checkout, .shopify-payment-button');
    if (!trigger) return;

    var input = inputForSubmit(t);
    if (!input) return;
    if (!saveTimer && !syncing) return;   /* nothing pending: let it through */

    event.preventDefault();
    var form = trigger.closest('form');
    var button = (trigger.tagName === 'BUTTON' && trigger.type === 'submit') ? trigger : null;

    flushSync(input).then(function () {
      submitAsCheckout(form, button);
    });
  }, true);

  /* ---------------------------------------------------------------------
     LEAVING
     --------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    if (reduced()) return;
    var t = event.target;
    if (!t || !t.closest) return;

    var trigger = t.closest('[data-juv-remove]');

    if (!trigger) {
      var step = t.closest('a.quantity-selector__button');
      if (!step) return;
      var href = step.getAttribute('href') || '';
      if (!/[?&]quantity=0(?:&|$)/.test(href)) return;
      trigger = step;
    }

    var item = trigger.closest('.juv-cart__item');
    if (item) item.setAttribute('data-juv-leaving', 'true');
  }, true);

  /* ---------------------------------------------------------------------
     REDUCED MOTION ON THE DRAWER ITSELF
     --------------------------------------------------------------------- */

  function settleDrawer(el) {
    try {
      ['content', 'overlay'].forEach(function (part) {
        var node = el.getShadowPartByName && el.getShadowPartByName(part);
        if (!node || !node.getAnimations) return;
        node.getAnimations().forEach(function (a) {
          try { a.finish(); } catch (e) { /* already finished */ }
        });
      });
    } catch (e) { /* vendor internals changed; motion simply stays on */ }
  }

  function patchDrawerMotion() {
    var Cls = window.customElements && window.customElements.get('cart-drawer');
    if (!Cls || !Cls.prototype || Cls.prototype.__juvPatched) return;

    ['createEnterAnimationControls', 'createLeaveAnimationControls'].forEach(function (name) {
      var original = Cls.prototype[name];
      if (typeof original !== 'function') return;
      Cls.prototype[name] = function () {
        var controls = original.apply(this, arguments);
        if (reduced()) settleDrawer(this);
        return controls;
      };
    });

    Cls.prototype.__juvPatched = true;
  }

  if (window.customElements && window.customElements.whenDefined) {
    window.customElements.whenDefined('cart-drawer').then(patchDrawerMotion).catch(function () {});
  }

  /* ---------------------------------------------------------------------
     RE-ARMING
     --------------------------------------------------------------------- */

  var syncTimer = null;

  /* A SURFACE RE-RENDERED BY THE THEME COMES BACK STALE. After Add to cart
     the vendor drawer re-renders itself from the cart BEFORE the note line
     has been written (the write follows the add on purpose), so its textarea
     and summary arrive empty while the product page still holds the words.
     If the customer has typed on this page, that input is the source:
     everything else is brought level with it before being painted. */
  function syncAll() {
    var inputs = allInputs();
    if (lastTyped && document.contains(lastTyped)) mirror(lastTyped);
    for (var i = 0; i < inputs.length; i++) render(inputs[i], true);
  }

  function scheduleSync() {
    if (writing) return;
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(syncAll, 40);
  }

  function observe() {
    syncAll();

    getCart().then(function (cart) {
      var line = noteLine(cart);
      syncDot(cart);
      if (line && realItemCount(cart) === 0) {
        return postJSON('cart/change.js', { id: line.key, quantity: 0 }).then(syncDot);
      }
      /* A note longer than the cap already in the cart -- pasted past an
         older limit, or posted to /cart/add.js by hand -- is cut down to the
         cap on the line item itself, so the order never receives more. */
      var stored = line && line.properties && line.properties.Message;
      if (typeof stored === 'string' && stored.length > cap()) {
        return postJSON('cart/change.js', { id: line.key, quantity: 1, properties: { Message: stored.slice(0, cap()).trim() } });
      }
      return null;
    }).catch(function () {});

    try {
      /* Ignore batches that happen entirely inside a note root: those are
         this file's own repaints (see the guard in paint()). A re-render that
         replaces the note wholesale mutates its PARENT, so it still syncs. */
      var mo = new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var t = records[i].target;
          var el = t.nodeType === 1 ? t : t.parentElement;
          if (!el || !el.closest('[data-juv-note]')) { scheduleSync(); return; }
        }
      });
      mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) { /* sync once and accept it */ }

    window.addEventListener('resize', scheduleSync);

    var imgs = document.querySelectorAll('[data-juv-note-card-img]');
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].complete) continue;
      imgs[i].addEventListener('load', scheduleSync, { once: true });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(scheduleSync).catch(function () {});
    }

    /* The message face loads lazily, on the first painted character or
       when the panel opens (the snippet warms it). Once it lands, the card
       re-lays out in the real metrics: repaint so the limit line reflects
       what is actually on the card. */
    if (document.fonts && document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingdone', scheduleSync);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observe);
  } else {
    observe();
  }
})();
